import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import './Quiz.css';

const QUESTION_TIME = 20;

export default function Quiz({ topic, navigate, onComplete }) {
  const { addXp, completeQuiz, checkAchievements, notify } = useGame();
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!topic) { navigate('lessons'); return; }
    fetch(`/api/quiz/${topic.id}`)
      .then(r => r.json())
      .then(data => { setQuestions(data); setLoading(false); })
      .catch(() => navigate('lessons'));
  }, [topic]);

  const handleTimeout = useCallback(() => {
    if (locked) return;
    setLocked(true);
    clearInterval(timerRef.current);
    const q = questions[current];
    setFeedback({ correct: false, explanation: q.explanation, correctAnswer: q.correct });
    setAnswers(prev => [...prev, { question_id: q.id, selected: -1, is_correct: false }]);
    setStreak(0);
    setTimeout(() => advance(), 2200);
  }, [locked, questions, current]);

  useEffect(() => {
    if (loading || locked) return;
    setTimeLeft(QUESTION_TIME);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleTimeout();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [current, loading, locked]);

  const advance = useCallback(() => {
    setFeedback(null);
    setSelected(null);
    setLocked(false);
    setCurrent(prev => prev + 1);
  }, []);

  const handleSelect = useCallback(async (optionIdx) => {
    if (locked) return;
    clearInterval(timerRef.current);
    setSelected(optionIdx);
    setLocked(true);

    const q = questions[current];
    const isCorrect = optionIdx === q.correct;
    const newStreak = isCorrect ? streak + 1 : 0;
    setStreak(newStreak);

    const newAnswer = { question_id: q.id, selected: optionIdx, is_correct: isCorrect };
    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    setFeedback({
      correct: isCorrect,
      explanation: q.explanation,
      correctAnswer: q.correct,
      streakBonus: newStreak >= 3,
    });

    const isLast = current + 1 >= questions.length;

    if (isLast) {
      // Submit to backend
      try {
        const res = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topic_id: topic.id,
            answers: newAnswers.map(a => ({ question_id: a.question_id, selected: a.selected })),
          }),
        });
        const result = await res.json();
        addXp(result.xp_earned);
        completeQuiz(topic.id, result, newAnswers);

        // Fetch achievements and check
        fetch('/api/achievements')
          .then(r => r.json())
          .then(achs => checkAchievements(achs));

        setTimeout(() => onComplete({ ...result, topicTitle: topic.title }), 2000);
      } catch {
        setTimeout(() => navigate('lessons'), 2000);
      }
    } else {
      setTimeout(() => advance(), 2000);
    }
  }, [locked, questions, current, streak, answers, topic, advance]);

  if (loading) return <div className="loading-state">Loading quiz...</div>;
  if (current >= questions.length) return <div className="loading-state">Finishing...</div>;

  const q = questions[current];
  const progress = ((current) / questions.length) * 100;
  const timerPercent = (timeLeft / QUESTION_TIME) * 100;

  return (
    <div className="quiz-wrap">
      <div className="quiz-topbar">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('lessons')}>
          &#8592; Exit Quiz
        </button>
        <div className="quiz-meta">
          <span className="quiz-topic-label">{topic.title}</span>
          <span className="quiz-progress-label">{current + 1} / {questions.length}</span>
        </div>
        {streak >= 3 && (
          <div className="streak-badge">&#128293; {streak} Streak!</div>
        )}
      </div>

      {/* Progress bar */}
      <div className="quiz-progress-bar">
        <div className="quiz-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Timer */}
      <div className="timer-bar-outer">
        <div
          className={`timer-bar-inner ${timeLeft <= 5 ? 'danger' : ''}`}
          style={{ width: `${timerPercent}%` }}
        />
      </div>

      <div className="quiz-card card animate-in" key={current}>
        <div className="quiz-header">
          <div className="quiz-q-num">Question {current + 1}</div>
          <div className={`timer-display ${timeLeft <= 5 ? 'danger' : ''}`}>{timeLeft}s</div>
        </div>

        <h2 className="quiz-question">{q.question}</h2>

        <div className="quiz-options">
          {q.options.map((opt, idx) => {
            let cls = 'quiz-option';
            if (locked) {
              if (idx === q.correct) cls += ' correct';
              else if (idx === selected && !feedback?.correct) cls += ' wrong';
            }
            if (selected === idx && !locked) cls += ' selected';

            return (
              <button key={idx} className={cls} onClick={() => handleSelect(idx)} disabled={locked}>
                <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                <span className="option-text">{opt}</span>
                {locked && idx === q.correct && <span className="option-check">&#10003;</span>}
                {locked && idx === selected && idx !== q.correct && <span className="option-x">&#10007;</span>}
              </button>
            );
          })}
        </div>

        {feedback && (
          <div className={`feedback-box ${feedback.correct ? 'correct' : 'wrong'} animate-in`}>
            <div className="feedback-header">
              <span className="feedback-icon">{feedback.correct ? '&#10003;' : '&#10007;'}</span>
              <span className="feedback-label">
                {feedback.correct
                  ? feedback.streakBonus
                    ? `Correct! &#128293; Streak bonus!`
                    : 'Correct!'
                  : selected === -1 ? 'Time\'s up!' : 'Incorrect'}
              </span>
            </div>
            <p className="feedback-explanation">{feedback.explanation}</p>
          </div>
        )}
      </div>

      <div className="quiz-score-row">
        {answers.map((a, i) => (
          <div key={i} className={`score-dot ${a.is_correct ? 'correct' : 'wrong'}`} />
        ))}
        {Array.from({ length: questions.length - answers.length }).map((_, i) => (
          <div key={`pending-${i}`} className="score-dot pending" />
        ))}
      </div>
    </div>
  );
}
